import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode } from 'react'
import { AcaraIcon } from './AcaraIcon'
import type { AcaraIconName } from './iconCatalog'

export type ProductStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export function StatusPill({ label, tone = 'neutral' }: { label: string; tone?: ProductStatusTone }) {
  const icon: Record<ProductStatusTone, AcaraIconName> = { neutral: 'clock', info: 'notification', success: 'verified', warning: 'warning', danger: 'critical' }
  return <span className={`product-status product-status--${tone}`}><AcaraIcon name={icon[tone]} size={16} /><span>{label}</span></span>
}

export function Breadcrumb({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return <button className="product-breadcrumb" type="button" onClick={onClick}><span aria-hidden="true">←</span>{children}</button>
}

export function EventOverviewCrumb({ eventId, onNavigate }: { eventId: string; onNavigate?: (path: string) => void }) {
  const go = () => {
    const path = `/app/organizer/events/${eventId}`
    if (onNavigate) onNavigate(path)
    else window.location.hash = path
  }
  return <Breadcrumb onClick={go}>Event overview</Breadcrumb>
}

export function PageHeader({ eyebrow, title, description, status, actions, breadcrumb }: { eyebrow?: string; title: string; description?: ReactNode; status?: ReactNode; actions?: ReactNode; breadcrumb?: ReactNode }) {
  return <header className="product-page-header">{breadcrumb}<div className="product-page-header__row"><div>{eyebrow ? <p className="product-eyebrow">{eyebrow}</p> : null}<h1>{title}</h1>{description ? <div className="product-page-header__description">{description}</div> : null}</div>{status || actions ? <div className="product-page-header__actions">{status}{actions}</div> : null}</div></header>
}

export function GlassCard({ children, className = '', as = 'section', ...props }: { children: ReactNode; className?: string; as?: 'section' | 'article' | 'aside' } & HTMLAttributes<HTMLElement>) {
  const Component = as
  return <Component className={`product-card ${className}`.trim()} {...props}>{children}</Component>
}

export function SectionHeader({ icon, eyebrow, title, description, action }: { icon?: AcaraIconName; eyebrow?: string; title: string; description?: ReactNode; action?: ReactNode }) {
  return <div className="product-section-header">{icon ? <span className="product-section-header__icon"><AcaraIcon name={icon} size={24} /></span> : null}<div>{eyebrow ? <p className="product-eyebrow">{eyebrow}</p> : null}<h2>{title}</h2>{description ? <div className="product-section-header__description">{description}</div> : null}</div>{action ? <div className="product-section-header__action">{action}</div> : null}</div>
}

export type StepDefinition = { id: string; label: string; description?: string; complete?: boolean; disabled?: boolean }

export function ProgressStepper({ steps, activeId, onSelect, label = 'Setup progress' }: { steps: StepDefinition[]; activeId: string; onSelect: (id: string) => void; label?: string }) {
  return <nav className="product-stepper" style={{ '--step-count': steps.length } as CSSProperties} aria-label={label}>{steps.map((step, index) => <button key={step.id} type="button" className={step.id === activeId ? 'active' : step.complete ? 'complete' : ''} disabled={step.disabled} aria-current={step.id === activeId ? 'step' : undefined} onClick={() => onSelect(step.id)}><span>{step.complete ? <AcaraIcon name="verified" size={16} /> : index + 1}</span><strong>{step.label}</strong>{step.description ? <small>{step.description}</small> : null}</button>)}</nav>
}

export type EventPhaseDefinition = { id: string; label: string; description?: string }

export function EventPhaseNav({ phases, activeId, onSelect, label = 'Event workspace phases' }: {
  phases: EventPhaseDefinition[]
  activeId: string
  onSelect: (id: string) => void
  label?: string
}) {
  return <nav className="event-phase-nav" aria-label={label}>{phases.map(phase => <button key={phase.id} type="button" className={phase.id === activeId ? 'active' : ''} aria-current={phase.id === activeId ? 'page' : undefined} title={phase.description} onClick={() => onSelect(phase.id)}><span>{phase.label}</span>{phase.description ? <small>{phase.description}</small> : null}</button>)}</nav>
}

export function TaskCard({ icon, eyebrow, title, description, facts, action, className = '' }: {
  icon: AcaraIconName
  eyebrow: string
  title: string
  description: ReactNode
  facts?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return <article className={`product-task-card ${className}`.trim()}><span className="product-task-card__icon"><AcaraIcon name={icon} size={24} /></span><div className="product-task-card__body"><p className="product-eyebrow">{eyebrow}</p><h2>{title}</h2><div className="product-task-card__description">{description}</div>{facts ? <div className="product-task-card__facts">{facts}</div> : null}</div>{action ? <div className="product-task-card__action">{action}</div> : null}</article>
}

export function EventWorkspaceHeader({ title, status, date, venue }: { title: string; status: string; date: string; venue?: string | null }) {
  const tone: ProductStatusTone = status === 'published' || status === 'live' || status === 'completed' ? 'success' : status === 'suspended' ? 'warning' : status === 'cancelled' ? 'danger' : 'neutral'
  return <section className="event-workspace-header"><div className="event-workspace-header__identity"><div><p className="product-eyebrow">Event workspace</p><h1>{title}</h1><p><AcaraIcon name="calendar" size={16} />{date}<span aria-hidden="true">·</span><AcaraIcon name="location" size={16} />{venue || 'Venue pending'}</p></div><StatusPill label={status.replaceAll('_', ' ')} tone={tone} /></div></section>
}

export function AuditDisclosure({ title = 'Technical & audit details', children }: { title?: string; children: ReactNode }) {
  return <details className="audit-disclosure"><summary><AcaraIcon name="audit-log" size={20} /><span>{title}</span><span aria-hidden="true">＋</span></summary><div className="audit-disclosure__body">{children}</div></details>
}

export function InfoBanner({ title, children, tone = 'info' }: { title: string; children: ReactNode; tone?: ProductStatusTone }) {
  const icon: Record<ProductStatusTone, AcaraIconName> = { neutral: 'notification', info: 'notification', success: 'verified', warning: 'warning', danger: 'critical' }
  return <section className={`product-banner product-banner--${tone}`}><AcaraIcon name={icon[tone]} size={24} /><div><strong>{title}</strong><div>{children}</div></div></section>
}

export function EmptyState({ icon = 'events', title, description, action }: { icon?: AcaraIconName; title: string; description: ReactNode; action?: ReactNode }) {
  return <section className="product-empty"><span><AcaraIcon name={icon} size={32} /></span><h2>{title}</h2><div>{description}</div>{action ? <div className="product-empty__action">{action}</div> : null}</section>
}

export function PrimaryButton({ className = '', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`product-button product-button--primary ${className}`.trim()} {...props}>{children}</button>
}

export function SecondaryButton({ className = '', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`product-button product-button--secondary ${className}`.trim()} {...props}>{children}</button>
}

export function DangerButton({ className = '', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`product-button product-button--danger ${className}`.trim()} {...props}>{children}</button>
}

/**
 * Registrable business forms an organizer can hold in Malaysia. Defined once and shared
 * by onboarding and organization settings: free text here produced inconsistent records
 * for one legal form ("Sdn Bhd", "sdn.bhd.", "Private Limited"), in a field that feeds
 * organizer verification. "Other" stays so an unusual form is recordable, not blocked.
 */
export const ENTITY_TYPES = [
  'Sendirian Berhad (Sdn. Bhd.)',
  'Berhad (Bhd.)',
  'Enterprise (sole proprietorship)',
  'Partnership',
  'Limited Liability Partnership (PLT)',
  'Society or association (Persatuan)',
  'Koperasi',
  'Individual (no business registration)',
  'Other',
] as const

export function EntityTypeOptions() {
  return <>
    <option value="">Select entity type</option>
    {ENTITY_TYPES.map(value => <option key={value} value={value}>{value}</option>)}
  </>
}
